import React from 'react';
import { RefreshCw } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { FiscalYearBanner } from '../../components/dashboard/FiscalYearBanner';
import { FinancialMetricsCard } from '../../components/dashboard/FinancialMetricsCard';
import { KeyPerformanceIndicatorsCard } from '../../components/dashboard/KeyPerformanceIndicatorsCard';
import { RecentActivitiesCard } from '../../components/dashboard/RecentActivitiesCard';
import { ActivityDemoButton, useActivityDemo } from '../../components/dashboard/ActivityDemo';
import { RevenueExpenseChart } from '../../components/charts/RevenueChart';
import { ExpensesPieChart } from '../../components/charts/ExpensesPieChart';
import { ChartsFilters } from '../../components/charts/ChartsFilters';
import { useChartsData } from '../../hooks/useChartsData';
import { useDashboard } from '../../hooks/useDashboard';
import { useNotifications } from '../../hooks/useNotifications';

export function DashboardPage() {
  const { dashboardData, loading, refreshDashboard } = useDashboard();
  const { addNotification } = useNotifications();

  // Activer les activités de démonstration
  useActivityDemo();

  // Hook pour les données des graphiques avec filtres
  const {
    revenueExpenseData,
    expenseBreakdownData,
    filters,
    setPeriod,
    updateDateRange,
    toggleRevenueVisibility,
    toggleExpensesVisibility,
    formatAmount,
    formatCompactAmount
  } = useChartsData();

  const {
    financialRatios,
    keyPerformanceIndicators,
    alerts
  } = dashboardData;
  
  // Fonction pour rafraîchir les données du dashboard
  const handleRefresh = () => {
    refreshDashboard();
    addNotification({
      title: 'Tableau de bord',
      message: 'Les données ont été rafraîchies avec succès',
      type: 'success'
    });
  };
  
  const [processedAlerts, setProcessedAlerts] = React.useState(new Set<string>());
  
  // Convertir les alertes en notifications au chargement de la page
  React.useEffect(() => {
    if (alerts && alerts.length > 0) {
      alerts.forEach(alert => {
        // Créer un ID unique pour cette alerte
        const alertId = `${alert.type}_${alert.message.substring(0, 50)}`;
        
        // Éviter les doublons
        if (!processedAlerts.has(alertId)) {
          setProcessedAlerts(prev => new Set(prev).add(alertId));
          addNotification({
            title: 'Alerte',
            message: alert.message,
            type: alert.type === 'success' ? 'success' : 
                  alert.type === 'warning' ? 'warning' : 'error'
          });
        }
      });
    }
  }, [alerts]); // ← Retirer addNotification des dépendances

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Tableau de Bord Comptable
        </h1>
        <Button 
          variant="outline"
          onClick={handleRefresh}
          disabled={loading}
          className="flex items-center"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      <FiscalYearBanner />
      
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Filtres des graphiques */}
          <ChartsFilters
            dateRange={filters.dateRange}
            showRevenue={filters.showRevenue}
            showExpenses={filters.showExpenses}
            onPeriodChange={setPeriod}
            onDateRangeChange={updateDateRange}
            onToggleRevenue={toggleRevenueVisibility}
            onToggleExpenses={toggleExpensesVisibility}
          />

          {/* Graphiques avec données filtrées */}
          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Évolution du CA et des Charges">
              <RevenueExpenseChart 
                data={revenueExpenseData}
                showRevenue={filters.showRevenue}
                showExpenses={filters.showExpenses}
                formatAmount={formatAmount}
                formatCompactAmount={formatCompactAmount}
              />
            </Card>

            <Card title="Répartition des Charges">
              <ExpensesPieChart 
                data={expenseBreakdownData}
                formatAmount={formatAmount}
              />
            </Card>
          </div>

          {/* Métriques financières et KPI en grid compact */}
          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-4">
            {financialRatios && <FinancialMetricsCard {...financialRatios} />}
            
            {keyPerformanceIndicators && (
              <KeyPerformanceIndicatorsCard
                creditScore={keyPerformanceIndicators.creditScore}
                financialRating={keyPerformanceIndicators.financialRating}
              />
            )}
          </div>

          {/* Activités récentes - pleine largeur en bas */}
          <div className="w-full">
            <RecentActivitiesCard />
          </div>
        </>
      )}
      
      {/* Bouton de démonstration pour tester les activités */}
      <ActivityDemoButton />
    </div>
  );
}